import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ConfigProvider } from "antd";
import React, { StrictMode, Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  matchPath,
  MiddlewareFunction,
  Outlet,
  redirect,
  replace,
  RouteObject,
  RouterProvider,
  useRouteError,
} from "react-router";

import * as HomeModule from "./features/home";
// This ts-ignore helps when the package is used as a dependency in the microfrontend remote
// because the file does not exist after checkout
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import auth0_config from "./auth0-config.json";
import {
  agentRoutes,
  areaRoutes,
  consumptionRoutes,
  dataSourcesRoutes,
  emsOverviewRoutes,
  energyFlowRoutes,
  energyPricesRoutes,
  multisiteRoutes,
  operatingStatesRoutes,
  planRoutes,
  reportsRoutes,
  trendRoutes,
  utilizationRoutes,
} from "./features/routes";
import {
  acceptInvitationRoutes,
  completeProfileRoutes,
  emailVerificationRoutes,
} from "./features/user";
import { settingsRoutes } from "./features/settings";
import { FeatureFlagService } from "./lib/feature-flag-service";
import { activeOrganizationChanged, activeOrganizationLoader } from "./lib/contexts/organization";
import { shouldRevalidateUserState, userStateLoader } from "./lib/contexts/user";
import { getAntdLocale } from "./lib/i18n";
import { LoadingScreen } from "./lib/ui-components/loading";
import { NotificationProvider } from "./lib/notification";
import {
  convertRouteModule,
  redirectToActiveOrganizationLoader,
  redirectToAppWithActiveOrganizationLoader,
} from "./lib/routing";
import { antdComponentConfig, antdTheme } from "./styles/theme";
import { GlobalLoading } from "./lib/ui-components/layout/global-loading-state";
import { EmsLayout } from "./ems";
import { ApplicationErrorPage, ErrorPage } from "./lib/ui-components/error/application-error-page";
import { MonitoringFallback } from "./features/ems/fallback/monitoring-fallback";
import {
  ErrorReportingBoundary,
  useWebAnalytics,
  WebAnalyticsProvider,
  WithOrganizationWebAnalytics,
} from "./lib/contexts/web-analytics";
import { AppLayout } from "./lib/ui-components/layout/app-layout";
import { TopbarLayout } from "./lib/ui-components/layout/topbar-layout";
import { peakLoadRoutes } from "./features/ems/peak-load/routes";
import { AuthService } from "./lib/auth";
import { actionPlanRoutes } from "./features/action-plan/routes";
import { CopilotProvider } from "./lib/contexts/copilot";

AuthService.init(auth0_config);
await AuthService.handleSigninRedirect();

function StyleProvider({ children }: React.PropsWithChildren) {
  const { i18n } = useLingui();

  return (
    <ConfigProvider locale={getAntdLocale(i18n.locale)} theme={antdTheme} {...antdComponentConfig}>
      <NotificationProvider>{children}</NotificationProvider>
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <StrictMode>
      <StyleProvider>
        <ErrorReportingBoundary FallbackComponent={ErrorPage}>
          <Suspense fallback={<LoadingScreen ariaLabel="Loading..." />}>
            <WebAnalyticsProvider>
              <AppRoutes />
            </WebAnalyticsProvider>
          </Suspense>
        </ErrorReportingBoundary>
      </StyleProvider>
    </StrictMode>
  );
}

function AppRoutes() {
  const webAnalytics = useWebAnalytics();

  useEffect(() => {
    AuthService.user().then((user) => {
      if (user) {
        webAnalytics.registerSuperProperties({
          is_internal_user:
            user?.email?.includes("@enit.io") || user?.email?.includes("@enit-systems.com"),
        });
      }
    });
  }, [webAnalytics]);

  async function forceLogin() {
    if (!(await AuthService.isAuthenticated())) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw replace(await AuthService.signin({ returnTo: window.location.pathname }));
    }
    return null;
  }

  function redirectWithOriginLoader(path: string) {
    return redirect(
      `${path}?${new URLSearchParams({ origin: window.location.pathname }).toString()}`
    );
  }

  async function featureFlagMiddleware(
    { params }: Parameters<MiddlewareFunction>[0],
    next: Parameters<MiddlewareFunction>[1]
  ) {
    await FeatureFlagService.setContext(params.orgaId);
    await next();
  }

  const routes: Array<RouteObject> = [
    {
      shouldRevalidate: shouldRevalidateUserState,
      middleware: [featureFlagMiddleware],
      loader: async (args) => {
        const user = await AuthService.user();
        if (!user) {
          return await forceLogin();
        } else if (!user.email_verified) {
          if (new URL(args.request.url).pathname !== "/user/verify-email") {
            return redirectWithOriginLoader("/user/verify-email");
          } else {
            return { user };
          }
        } else {
          return userStateLoader(args);
        }
      },
      id: "user-state",
      children: [
        {
          element: <TopbarLayout />,
          children: [
            { path: "user/complete-profile", children: completeProfileRoutes },
            { path: "user/accept-invitation", children: acceptInvitationRoutes },
            { path: "user/verify-email", children: emailVerificationRoutes },
          ],
        },
        {
          path: "admin",
          children: [
            {
              path: "*",
              children: [
                {
                  path: "carbon",
                  handle: {
                    lazyChildren: {
                      "/admin/carbon/*": async () => {
                        // @ts-expect-error this is a federated module and can only be resolved at runtime
                        const { carbonAdminRoutes } = await import("carbon/routes");
                        return [{ path: "*", children: carbonAdminRoutes }];
                      },
                    },
                  },
                },
                {
                  path: "*",
                  lazy: async () => convertRouteModule(await import("./features/admin/admin")),
                },
              ],
            },
          ],
        },
        {
          path: "user/profile",
          element: <TopbarLayout />,
          children: [
            {
              index: true,
              lazy: async () => convertRouteModule(await import("./features/user/profile")),
            },
          ],
        },
        {
          path: "app",
          loader: redirectToAppWithActiveOrganizationLoader,
          children: [
            { index: true },
            {
              path: ":appName",
              children: [
                /*
                This needs to have the extra level of nesting with an index route to increase its
                ranking score to make sure a route like /app/carbon doesn't get matched by
                /:orgaId/carbon
                https://reactrouter.com/en/main/start/concepts#ranking-routes
                ^ this explains the basics of ranked routes, but I haven't been able to find
                exact numbers for the ranking scores
            */
                { index: true },
              ],
            },
          ],
        },
        {
          path: ":orgaId",
          id: "organizationId",
          loader: activeOrganizationLoader,
          shouldRevalidate: activeOrganizationChanged,
          element: (
            <WithOrganizationWebAnalytics>
              <CopilotProvider>
                <GlobalLoading />
              </CopilotProvider>
            </WithOrganizationWebAnalytics>
          ),
          handle: {
            lazyChildren: {
              "/:orgaId/carbon/*": async () => {
                // @ts-expect-error this is a federated module and can only be resolved at runtime
                const { carbonRoutes } = await import("carbon/routes");
                return [{ path: "carbon", children: carbonRoutes }];
              },
            },
          },
          children: [
            { index: true, loader: () => redirect("home") },
            {
              element: <TopbarLayout />,
              children: [{ path: "home", ...convertRouteModule(HomeModule) }],
            },
            {
              path: "settings",
              element: <Outlet />,
              children: settingsRoutes,
            },
            {
              element: <EmsLayout />,
              errorElement: <EmsLayout children={<ApplicationErrorPage />} />,
              children: [
                {
                  errorElement: <MonitoringFallback />,
                  children: [
                    {
                      path: "energy-overview",
                      children: emsOverviewRoutes,
                      element: <AppLayout name={t`Energy Overview`} />,
                    },
                    {
                      path: "agent",
                      children: agentRoutes,
                      element: <AppLayout name={t`Enit agent`} />,
                    },
                    {
                      path: "area",
                      children: areaRoutes,
                      element: <AppLayout name={t`Areas`} />,
                    },
                    {
                      path: "reports",
                      children: reportsRoutes,
                      element: <AppLayout name={t`Reports`} />,
                    },
                    {
                      path: "consumption",
                      children: consumptionRoutes,
                      element: <AppLayout name={t`Consumption`} />,
                    },
                    {
                      path: "trend",
                      children: trendRoutes,
                      element: <AppLayout name={t`Trend`} />,
                    },
                    {
                      path: "plan",
                      children: planRoutes,
                      element: <AppLayout name={t`Measuring Concept`} />,
                    },
                    {
                      path: "energy-flow",
                      children: energyFlowRoutes,
                      element: <AppLayout name={t`Energy Flow`} />,
                    },
                    {
                      path: "operating-states",
                      children: operatingStatesRoutes,
                      element: <AppLayout name={t`Operating States`} />,
                    },
                    {
                      path: "utilization",
                      children: utilizationRoutes,
                      element: <AppLayout name={t`Utilization`} />,
                    },
                    {
                      path: "peak-load",
                      children: peakLoadRoutes,
                      element: <AppLayout name={t`Peak Loads`} />,
                    },
                    {
                      path: "multisite",
                      children: multisiteRoutes,
                      element: <AppLayout name={t`Multisite`} />,
                    },
                    {
                      path: "data-sources",
                      children: dataSourcesRoutes,
                      element: <AppLayout name={t`Data Sources`} />,
                    },
                    {
                      path: "energy-prices",
                      children: energyPricesRoutes,
                      element: <AppLayout name={t`Energy Prices`} />,
                    },
                    {
                      path: "action-plan",
                      children: actionPlanRoutes,
                      element: <AppLayout name={t`Actions`} />,
                    },
                  ],
                },
              ],
            },
            { path: "*", loader: ({ params }) => redirect(`/${params.orgaId}/home`) },
          ],
        },
        { path: "*", element: null, loader: redirectToActiveOrganizationLoader },
      ],
    },
  ];

  return (
    <RouterProvider
      router={createBrowserRouter(
        [
          {
            children: routes,
            hydrateFallbackElement: <LoadingScreen ariaLabel="Loading data" />,
            element: <Outlet />,
            ErrorBoundary: () => {
              // this is a workaround to let errors thrown *within* the router bubble up
              // so that we can de-couple the router from the error boundary
              throw useRouteError();
            },
          },
        ],
        {
          async patchRoutesOnNavigation({ path, matches, patch }) {
            const leafRoute = matches[matches.length - 1]?.route;
            if (leafRoute.handle?.lazyChildren) {
              for (const [p, f] of Object.entries<() => Promise<Array<RouteObject>>>(
                leafRoute.handle?.lazyChildren
              )) {
                if (matchPath(p, path)) {
                  patch(leafRoute.id!, await f());
                }
              }
            }
          },
        }
      )}
    />
  );
}
